import { inject, Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import { firstValueFrom, tap } from 'rxjs';
import { MessageThread } from '../../../domain/message/model/message-thread';
import { MessageThreadCriteria } from '../../../domain/message/model/message-thread-criteria';
import { isDataCached } from '../../../shared/helper/is-data-cached';
import { CacheDataActions } from '../../../shared/store/cache-data/cache-data.actions';
import { CacheDataState } from '../../../shared/store/cache-data/cache-data.state';
import { MessageApiService } from '../service/message/message-api.service';
import { MessageActions } from './message.actions';
import { Page } from '../../../shared/model/local/pagination/page';

export type MessageStateModel = Record<string, unknown>;

const defaults: MessageStateModel = {};

@State<MessageStateModel>({
  name: 'message',
  defaults,
})
@Injectable()
export class MessageState {
  private static readonly messageListCacheKey = 'message-thread-list';

  static getMessageListCacheKey(): string {
    return MessageState.messageListCacheKey;
  }

  private readonly messageApiService = inject(MessageApiService);

  static page() {
    return createSelector(
      [CacheDataState.getDataByKey(MessageState.messageListCacheKey)],
      (data: unknown) => data as Page<MessageThread> | undefined
    );
  }

  static criteria() {
    return createSelector(
      [CacheDataState.getCriteriaByKey(MessageState.messageListCacheKey)],
      (data: unknown) => data as MessageThreadCriteria | undefined
    );
  }

  @Action(MessageActions.GetPage, { cancelUncompleted: true })
  async getPage(
    ctx: StateContext<MessageStateModel>,
    { criteria }: MessageActions.GetPage
  ) {
    const cacheKey = MessageState.messageListCacheKey;
    if (isDataCached(cacheKey, criteria, 1000 * 5)) {
      await this.fetchPageData(ctx, criteria);
    }
    return;
  }

  @Action(MessageActions.ArchiveThread)
  archiveThread(
    ctx: StateContext<MessageStateModel>,
    { caseId }: MessageActions.ArchiveThread
  ) {
    return this.messageApiService
      .archiveMessageThread(caseId)
      .pipe(tap(() => ctx.dispatch(new MessageActions.InvalidateCache())));
  }

  @Action(MessageActions.RefreshPage)
  async refreshPage(ctx: StateContext<MessageStateModel>) {
    return this.fetchPageData(ctx);
  }

  @Action(MessageActions.InvalidateCache)
  invalidateCache(ctx: StateContext<MessageStateModel>) {
    return ctx.dispatch(
      new CacheDataActions.InvalidateCache(MessageState.messageListCacheKey)
    );
  }

  private async fetchPageData(
    ctx: StateContext<MessageStateModel>,
    criteria?: MessageThreadCriteria
  ) {
    const store = inject(Store);
    const updateCriteria =
      criteria ??
      store.selectSnapshot(
        CacheDataState.getCriteriaByKey(MessageState.messageListCacheKey)
      );

    const page = await firstValueFrom(
      this.messageApiService.getMessageThreadPage(
        updateCriteria ?? new MessageThreadCriteria()
      )
    );

    ctx.dispatch(
      new CacheDataActions.SetCachedData(
        MessageState.messageListCacheKey,
        page,
        updateCriteria ?? {}
      )
    );
  }
}
